// "Path of Survival" — a respectful, decision-based narrative inspired by
// publicly documented survivor accounts of the August 2014 events.
// No graphic content. Outcomes reflect historical realities.
// The player is a Yazidi parent in a village near Sinjar town.

export const GAME = {
  start: "intro",
  nodes: {
    intro: {
      id: "intro",
      stage: "Dawn — August 3, 2014",
      text:
        "It is just before dawn. You wake to the sound of distant gunfire and a frantic neighbor at the door. ISIS has crossed into Sinjar. The Peshmerga have withdrawn in the night. Your spouse is asleep. Your two children — eight and twelve — are still in bed.",
      choices: [
        { label: "Wake the family. Pack water. We leave immediately.", next: "leave_early", testid: "choice-leave-early" },
        { label: "Try the phone. Call cousins in Sinjar town for news first.", next: "call_first", testid: "choice-call-first" },
        { label: "Hide in the cellar. Wait for the Peshmerga to return.", next: "hide_cellar", testid: "choice-hide-cellar" },
      ],
    },

    leave_early: {
      id: "leave_early",
      stage: "Early morning — the road",
      text:
        "You move in minutes. Two jerry cans of water, bread, ID papers, your wife's gold. You take the small road north — toward Mount Sinjar. The horizon is already smoke. Other families pour onto the road behind you.",
      choices: [
        { label: "Push hard for the mountain on foot.", next: "mountain_climb", testid: "choice-mountain-climb" },
        { label: "Try to flag a passing pickup truck.", next: "truck_ride", testid: "choice-truck-ride" },
      ],
    },

    call_first: {
      id: "call_first",
      stage: "An hour lost",
      text:
        "The line is busy, then dead. By the time you understand the scale of it, ISIS checkpoints have closed the main road north. There is one option left.",
      choices: [
        { label: "Take the back trail through the fields toward the mountain.", next: "fields_trail", testid: "choice-fields-trail" },
        { label: "Hide in the cellar. Hope they pass through.", next: "hide_cellar", testid: "choice-hide-cellar" },
      ],
    },

    hide_cellar: {
      id: "hide_cellar",
      stage: "Below the house",
      text:
        "You bring the children below. You leave the door ajar so it looks abandoned. For two days you hear boots, voices, doors kicked in. On the third day there is silence. You climb out. The village is empty. The neighbors are gone.",
      outcome: {
        type: "bittersweet",
        title: "Survived — but separated",
        body:
          "You and your children survive, but you have lost everyone you knew. You walk for three days to the mountain corridor and reach a UN camp near Dohuk. Years later you will still search lists for the names of cousins and friends.",
        survivors: 3,
        historical:
          "Many Yazidis who hid in cellars survived the initial assault but lost extended family and entire villages. Some are still searching today.",
      },
    },

    truck_ride: {
      id: "truck_ride",
      stage: "On the road",
      text:
        "A pickup stops. A young man — a stranger, also Yazidi — waves you in. The truck is dangerously full. He drives without lights toward Mount Sinjar.",
      choices: [
        { label: "Stay on the truck all the way up.", next: "mountain_truck", testid: "choice-mountain-truck" },
        { label: "Get off near the foothills and walk the rest. The truck is too obvious.", next: "mountain_climb", testid: "choice-foothills-walk" },
      ],
    },

    fields_trail: {
      id: "fields_trail",
      stage: "Midday — under the sun",
      text:
        "You move through dry wheat fields. Your youngest is exhausted. Your spouse has blistered feet. You see the mountain in the distance — a brown wall under a white sky.",
      choices: [
        { label: "Keep moving. Don't stop until the foothills.", next: "mountain_climb", testid: "choice-keep-moving" },
        { label: "Rest in the shade of an irrigation channel for an hour.", next: "rest_channel", testid: "choice-rest-channel" },
      ],
    },

    rest_channel: {
      id: "rest_channel",
      stage: "The irrigation channel",
      text:
        "The hour stretches to two. When you rise, an ISIS patrol vehicle is in the next field. You are seen. There is only one direction to run.",
      choices: [
        { label: "Run toward the mountain — leave everything behind.", next: "mountain_climb_late", testid: "choice-run-mountain" },
      ],
    },

    mountain_truck: {
      id: "mountain_truck",
      stage: "Mount Sinjar — the slopes",
      text:
        "The truck makes it halfway. From there you climb on foot in a column of thousands. Children are crying. People share their last water. Helicopters circle far overhead.",
      choices: [
        { label: "Stay with the column. Wait for organized rescue.", next: "siege_wait", testid: "choice-siege-wait" },
        { label: "Push further west — there are rumors of a corridor.", next: "corridor_west", testid: "choice-corridor-west" },
      ],
    },

    mountain_climb: {
      id: "mountain_climb",
      stage: "Mount Sinjar — the climb",
      text:
        "You climb in the heat. The air is thin and the stones are loose. Your eldest gives water to a younger child who is not yours. You reach a flat ridge by nightfall, where thousands have gathered.",
      choices: [
        { label: "Stay with the column. Wait for organized rescue.", next: "siege_wait", testid: "choice-siege-wait-2" },
        { label: "Push further west — there are rumors of a corridor.", next: "corridor_west", testid: "choice-corridor-west-2" },
      ],
    },

    mountain_climb_late: {
      id: "mountain_climb_late",
      stage: "The late climb",
      text:
        "You reach the foothills by dusk, exhausted, with very little water. You climb through the night. At dawn you find the column.",
      choices: [
        { label: "Find a Kurdish fighter and ask about the western corridor.", next: "corridor_west", testid: "choice-ask-fighter" },
        { label: "Stay with the column and wait.", next: "siege_wait", testid: "choice-stay-wait" },
      ],
    },

    siege_wait: {
      id: "siege_wait",
      stage: "Days on the mountain",
      text:
        "Days pass. Airdrops bring some water, but not enough. Two elderly people in your group do not survive the heat. A helicopter eventually lifts the most fragile children to Dohuk.",
      outcome: {
        type: "survived",
        title: "Survived the siege",
        body:
          "After eight days, the corridor finally reaches you and your family is evacuated through Syria back into Iraqi Kurdistan. You will live in a tent camp for years. You are alive. You will never forget the names of the elders who did not survive the mountain.",
        survivors: 4,
        historical:
          "Hundreds — many of them children and elderly — died of dehydration during the siege of Mount Sinjar. International airdrops and the YPG/PKK corridor saved tens of thousands of lives.",
      },
    },

    corridor_west: {
      id: "corridor_west",
      stage: "The western corridor",
      text:
        "You walk west along the ridges for two days. Kurdish YPG fighters meet your group at the Syrian side and guide you down into Rojava. From there, buses take you through Syria and back across the border into Iraqi Kurdistan.",
      outcome: {
        type: "survived",
        title: "Through the corridor",
        body:
          "Your family arrives at a UN camp near Dohuk on August 13. You are alive — together. The road ahead is long: years of displacement, the search for missing relatives, the slow work of rebuilding. But you are alive, together.",
        survivors: 4,
        historical:
          "The YPG/PKK humanitarian corridor through Syria evacuated an estimated 30,000–50,000 Yazidis from Mount Sinjar in August 2014.",
      },
    },
  },
};
